/* ============================================================
   ProviderOrdersPanel — incoming order queue: accept/reject/track
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import type { ProviderOrder, ProviderOrderPage, OrderStatus } from '../types/provider';
import { normalizeOrderStatus } from '../types/product';
import { SlaBadge } from './SlaBadge';
import { CueChip } from './CueChip';
import { chatUnreadLabel } from './orderChatView';
import { CounterOfferForm } from './OrdersPage';
import { OrderProgressStrip } from './OrderTimeline';
import { OrderChatPanel } from './OrderChatPanel';
import { Model3DPreview } from './Model3DPreview';
import { SlicingSection } from './SlicingSection';
import { ProviderPrintJobsCard } from './ProviderPrintJobsCard';
import { NalogReceiptSection } from './NalogReceiptSection';
import { buildProviderOrderView, providerNextStatus, providerTimelineInput } from './providerOrderView';
import { buildDeliveryAddressStrip, deliveryAddressStripText } from './deliveryAddressStrip';
import { buildShippedTrackingView, isPickupDeliveryMethod } from './shippedTrackingView';
import { FilterChip } from './FilterChip';
import { ListPager } from './ListPager';

/** 4171ba09: rows per page for the provider queue list. */
const ORDERS_PAGE_SIZE = 10;

interface ResultPhotoDraft {
  url: string;
  caption: string;
}

export function ProviderOrdersPanel() {
  // 4171ba09: server-side pagination — one page of slim rows plus the
  // bucket chip counts over the whole queue; the cabinet no longer dumps
  // every order (with its inline chat) into one list.
  const [pageData, setPageData] = useState<ProviderOrderPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'incoming' | 'active' | 'done'>('all');
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = useCallback(async (f: 'all' | 'incoming' | 'active' | 'done', off: number) => {
    setLoading(true);
    try {
      setPageData(await api.getProviderOrdersPage(f, off, ORDERS_PAGE_SIZE));
    } catch {
      // offline — empty state
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(filter, offset); }, [load, filter, offset]);

  const reload = useCallback(() => { void load(filter, offset); }, [load, filter, offset]);

  const changeFilter = (f: 'all' | 'incoming' | 'active' | 'done') => {
    setFilter(f);
    setOffset(0);
  };

  const orders = pageData?.items ?? [];

  const handleAccept = async (orderId: string) => {
    try {
      await api.acceptProviderOrder(orderId);
      await load(filter, offset);
    } catch { /* ignore */ }
  };

  const handleReject = async (orderId: string) => {
    try {
      await api.rejectProviderOrder(orderId);
      await load(filter, offset);
    } catch { /* ignore */ }
  };

  const handleAdvance = async (order: ProviderOrder) => {
    // TASK-253: canonical advance map lives in providerOrderView —
    // Printed → Shipped stays gated on customer confirmation (TASK-234).
    // Normalize first: the API serializes enums as numbers.
    const nextStatus = providerNextStatus(normalizeOrderStatus(order.status));
    if (!nextStatus) return;
    try {
      await api.updateProviderOrderStatus(order.orderId, nextStatus);
      await load(filter, offset);
    } catch { /* ignore */ }
  };

  // 4171ba09: chip labels over the server-side bucket counts — the counts
  // cover the whole queue, not just the loaded page (filtering is
  // server-side now, so the chips stay truthful under pagination).
  const bucketCounts = pageData?.bucketCounts ?? {};

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}>
        <div className="spinner" />
      </div>
    );
  }

  const selected = orders.find((o) => o.orderId === selectedId) ?? null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Заказы</h2>

      {selected ? (
        <div className="order-detail-layout">
          {/* 4171ba09: left = full information + available actions; the chat
              moved to the sticky right sidebar (.order-detail-chat). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
              <button className="btn btn-tertiary" style={{ height: 40, padding: '8px 16px' }} onClick={() => setSelectedId(null)}>
                ← К списку
              </button>
              <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
                Заказ №{selected.orderId.slice(0, 8)} · {selected.customerName}
              </h3>
            </div>
            <ProviderOrderDetail
              order={selected}
              onAccept={handleAccept}
              onReject={handleReject}
              onAdvance={handleAdvance}
              onChanged={reload}
            />
          </div>
          <aside className="order-detail-chat">
            {/* 33f30c97 (Artem 2026-08-28 §3): order chat — available on every
                status; the backend thread view carries the posting gate and
                lock copy, so the panel never second-guesses the state machine. */}
            <OrderChatPanel orderId={selected.orderId} role="provider" />
          </aside>
        </div>
      ) : (pageData?.total ?? 0) === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
            {/* TASK-255: filter-aware empty state — say what lands in this
                tab instead of a generic “nothing here”. */}
            {filter === 'all' ? 'Заказов пока нет. Опубликуйте принтеры и услуги — заказы клиентов появятся здесь.'
              : filter === 'incoming' ? 'Новых заказов нет. Когда клиент направит заказ, он появится здесь для принятия.'
              : filter === 'active' ? 'Активных заказов нет. Принятые в работу и спорные заказы будут видны здесь.'
              : 'Завершённых и отменённых заказов пока нет.'}
          </p>
        </div>
      ) : (
        <>
          {/* Filter chips — TASK-256: wrap on narrow screens */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {(['all', 'incoming', 'active', 'done'] as const).map((f) => (
              <FilterChip
                key={f}
                active={filter === f}
                onClick={() => changeFilter(f)}
                label={
                  f === 'all' ? `Все (${bucketCounts.all ?? 0})` :
                  f === 'incoming' ? `Новые (${bucketCounts.incoming ?? 0})` :
                  f === 'active' ? `В работе (${bucketCounts.active ?? 0})` :
                  `Готово (${bucketCounts.done ?? 0})`
                }
              />
            ))}
          </div>

          {/* 4171ba09: slim rows — number, dates, status, counterparty,
              total and the unread-chat badge; actions and chat live on the
              detail page. */}
          <div style={{ display: 'grid', gap: 'var(--space-md)' }}>
            {orders.map((order) => (
              <ProviderOrderRow key={order.orderId} order={order} onOpen={() => setSelectedId(order.orderId)} />
            ))}
          </div>
          <ListPager
            total={pageData?.total ?? 0}
            offset={offset}
            limit={ORDERS_PAGE_SIZE}
            onPrev={() => setOffset(Math.max(0, offset - ORDERS_PAGE_SIZE))}
            onNext={() => setOffset(offset + ORDERS_PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}

/* ── 4171ba09: slim list row ───────────────────────── */

function ProviderOrderRow({ order, onOpen }: { order: ProviderOrder; onOpen: () => void }) {
  const status = normalizeOrderStatus(order.status);
  const pickup = isPickupDeliveryMethod(order.deliveryMethod);
  const unread = order.unreadChatMessages ?? 0;
  // c3e7f6d0: address strip on the slim row too — the provider scans the
  // queue without opening cards (Artem @b8810bf1: самовывоз → точка,
  // курьер продавца → адрес клиента, ПВЗ/почта → только служба).
  const addressStrip = buildDeliveryAddressStrip({
    deliveryMethod: order.deliveryMethod,
    shippingAddress: order.shippingAddress,
    pickupPointWorkingHours: order.pickupPointWorkingHours,
    deliveryCarrier: order.deliveryCarrier,
  });
  return (
    <button
      className="card"
      style={{ textAlign: 'left', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}
      onClick={onOpen}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {/* s12/F4: derived 8-char № — same convention as OrdersPage. */}
          № {order.orderId.slice(0, 8)} · {new Date(order.createdAtUtc).toLocaleDateString('ru-RU')}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {/* 10fb5d39: customer chat is waiting — same wording/tone as the
              chat panel's unread badge, so row and thread never disagree. */}
          {unread > 0 && (
            <span className="badge badge-warning">💬 {chatUnreadLabel(unread)}</span>
          )}
          {/* TASK-ed21c5e1: confirmed Printed says whose court the ball is in. */}
          <StatusBadge
            status={status}
            confirmed={status === 'Printed' && order.customerConfirmedAtUtc != null}
            pickup={pickup}
          />
          {/* TASK-243 (G6): overdue / due-soon indicator. */}
          <SlaBadge sla={order.sla} deliveryMethod={order.deliveryMethod} />
        </span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        {/* 4171ba09: counterparty line — the customer who placed the order. */}
        <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>
          {order.customerName}
        </span>
        <span style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
          {order.totalPrice}₽
        </span>
      </div>
      <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>
        {order.modelFileName} · {order.materialName}{providerColorCue(order.printColor, order.printColorMode)}
      </span>
      {addressStrip && (
        <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          {deliveryAddressStripText(addressStrip)}
        </span>
      )}
    </button>
  );
}

/* ── Order detail (4171ba09: full card, opened from a slim row) ─ */

function ProviderOrderDetail({
  order, onAccept, onReject, onAdvance, onChanged,
}: {
  order: ProviderOrder;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAdvance: (o: ProviderOrder) => void;
  onChanged: () => void;
}) {
  const [showResultForm, setShowResultForm] = useState(false);
  const [showProgressForm, setShowProgressForm] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showShipForm, setShowShipForm] = useState(false);
  const [showTrackingForm, setShowTrackingForm] = useState(false);
  const [busyOffer, setBusyOffer] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const status = normalizeOrderStatus(order.status);
  const openOffer = order.openCounterOffer ?? null;
  // TASK-253: scannable card composition — one primary action, explicit
  // waiting states and cue chips all come from the pure view model.
  // Feed it the normalized status: the API serializes enums as numbers
  // and the view model switches on the string union (TASK-254 fix — the
  // raw number fell through the switch and crashed the queue render).
  const view = buildProviderOrderView({ ...order, status });
  // TASK-252: pickup handoff copy for the ship control/badge — same shared
  // predicate as the view model; legacy rows without a method snapshot
  // keep the courier wording byte-identical.
  const pickup = isPickupDeliveryMethod(order.deliveryMethod);

  const runPrimary = () => {
    if (!view.primary) return;
    switch (view.primary.kind) {
      case 'accept':
        onAccept(order.orderId);
        break;
      case 'startPrint':
      case 'complete':
        onAdvance(order);
        break;
      case 'attachResult':
        setShowResultForm((v) => !v);
        break;
      case 'ship':
        setShowShipForm((v) => !v);
        break;
    }
  };

  const respondOffer = async (accept: boolean) => {
    if (!openOffer) return;
    setBusyOffer(true);
    setOfferError(null);
    try {
      if (accept) {
        await api.acceptCounterOffer(order.orderId, openOffer.offerId);
      } else {
        await api.rejectCounterOffer(order.orderId, openOffer.offerId);
      }
      onChanged();
    } catch (e) {
      setOfferError(e instanceof Error ? e.message : 'Не удалось отправить ответ');
    } finally {
      setBusyOffer(false);
    }
  };

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {/* Top row — TASK-256: wraps on narrow screens (title block vs price) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
              {order.customerName}
            </h3>
            {/* TASK-ed21c5e1: confirmed Printed says whose court the ball is in —
                the customer badge flips to «Ждёт отправки» (TASK-258); the
                provider card said «Ждёт подтверждения» next to a
                «✓ Подтверждён клиентом» chip and an active ship button. */}
            <StatusBadge
              status={status}
              confirmed={status === 'Printed' && order.customerConfirmedAtUtc != null}
              pickup={pickup}
            />
            {/* TASK-243 (G6): overdue / due-soon indicator.
                TASK-255: method snapshot → pickup stage word «выдача». */}
            <SlaBadge sla={order.sla} deliveryMethod={order.deliveryMethod} />
            {/* 10fb5d39 (audit-11 §5 Card B): customer chat is waiting —
                same wording/tone as the chat panel's unread badge, so the
                queue card and the thread never disagree. */}
            {(order.unreadChatMessages ?? 0) > 0 && (
              <span className="badge badge-warning">
                💬 {chatUnreadLabel(order.unreadChatMessages!)}
              </span>
            )}
          </div>
          {/* s12/F4: derived 8-char № — same convention as OrdersPage detail,
              AdminPanel and emails; no domain order-number field exists. */}
          <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
            № {order.orderId.slice(0, 8)} · {order.modelFileName} · {order.materialName}{providerColorCue(order.printColor, order.printColorMode)} · ~{formatMinutes(order.estimatedPrintMinutes)}
          </p>
        </div>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
          {order.totalPrice}₽
        </p>
      </div>

      {/* cc4909b4: interactive 3D preview — the performer inspects the
          print target right on the queue card (raw mesh via the party-scoped
          /models/{id}/content, tinted with the spool color); ME-2 PNG stays
          the fallback for STEP / no-WebGL / load failures. */}
      {order.modelFileId ? (
        <Model3DPreview
          fileId={order.modelFileId}
          format={order.modelFileFormat}
          previewUrl={order.modelPreviewUrl}
          fileName={order.modelFileName}
          colorName={order.printColor}
          alt={`Модель ${order.modelFileName}`}
          height={128}
          style={{ width: 128, flexShrink: 0 }}
        />
      ) : order.modelPreviewUrl ? (
        <img
          src={resolveMediaUrl(order.modelPreviewUrl)}
          alt={`Рендер модели ${order.modelFileName}`}
          style={{
            display: 'block',
            width: 128,
            height: 128,
            objectFit: 'contain',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-separator)',
            background: 'var(--color-bg)',
          }}
        />
      ) : null}

      {/* TASK-253: scannable cue chips — photo counts, confirmation,
          negotiation state — before anything else on the card. */}
      {view.cues.length > 0 && (
        <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
          {view.cues.map((cue) => (
            <CueChip key={cue.label} label={cue.label} tone={cue.tone} />
          ))}
        </div>
      )}

      {/* TASK-251: compact status timeline + provider next action.
          da2f1e1b: input now comes from providerTimelineInput — the strip
          shares the customer timeline's dated milestones (result
          submission, confirmation, completion, dispute stamp). */}
      <OrderProgressStrip input={providerTimelineInput({ ...order, status })} />

      {/* 16215cbc: print-file preparation — headless Bambu Studio CLI
          slicing for the provider's printers; artifacts download right
          here. Hidden until the order is accepted or a job exists. */}
      <SlicingSection orderId={order.orderId} />

      {/* 511c826a: Bambu print actions — send a completed .gcode.3mf
          straight to the LAN-bound printer, then pause/resume/stop and
          refresh live progress; quiet until an artifact or job exists. */}
      <ProviderPrintJobsCard orderId={order.orderId} />

      {/* 15c2a311: per-order «Мой налог» receipt — formed automatically at
          payout time; the block stays hidden until a receipt exists. */}
      <NalogReceiptSection orderId={order.orderId} />

      {/* Details */}
      {(order.trackingNumber || order.estimatedDeliveryUtc || order.notes) && (
        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          {/* 31c9ace7: carrier-labeled tracking + «Отследить» link — the
              checkout carrier snapshot (what the customer's tariff says)
              wins over the number-shape guess; same pure view as the
              customer surface, so both parties see the same label. */}
          {order.trackingNumber && (() => {
            const trackingView = buildShippedTrackingView({
              trackingNumber: order.trackingNumber,
              deliveryMethod: order.deliveryMethod,
              deliveryCarrier: order.deliveryCarrier,
            });
            const labeled = trackingView.carrierLabel != null
              ? `${trackingView.trackingNumber} · ${trackingView.carrierLabel}`
              : (trackingView.trackingNumber ?? '');
            return (
              <>
                <Detail label="Трек" value={labeled} />
                {trackingView.trackUrl != null && (
                  <a
                    href={trackingView.trackUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-medium)', alignSelf: 'center' }}
                  >
                    {trackingView.trackLabel} →
                  </a>
                )}
              </>
            );
          })()}
          {/* TASK-254: method-aware ETA chip — pickup orders promise a
              collection day («Выдача»), courier/legacy keep «Доставка»; the
              label now matches the ShipForm date title and the overdue nudge
              that already speak про выдачу for the same field. */}
          {view.eta && <Detail label={view.eta.label} value={view.eta.text} />}
          {order.notes && <Detail label="Заметка" value={order.notes} />}
        </div>
      )}

      {/* G8-D1: how the customer wants the order delivered — method + cost
          snapshot («Самовывоз — бесплатно» / «Курьер — 350 ₽"). */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
        <Detail label="Способ получения" value={view.delivery.text} />
        {/* G8-D2: the chosen self-pickup point with its working hours — the
            provider needs to know where (and when) the customer will collect. */}
        {view.pickupPoint && (
          <Detail
            label="Пункт самовывоза"
            value={view.pickupPoint.workingHours
              ? `${view.pickupPoint.address} · ${view.pickupPoint.workingHours}`
              : view.pickupPoint.address}
          />
        )}
        {/* audit-3 A2: where to deliver courier orders — the address always
            rode ProviderOrderDto.ShippingAddress but was never shown. */}
        {view.courierAddress && (
          <Detail label="Адрес доставки" value={view.courierAddress} />
        )}
        {/* c3e7f6d0 (Artem @b8810bf1): ПВЗ/почтовые службы — service name
            ONLY: the carrier runs the last mile, the provider hands the
            parcel over; the point address never renders provider-side. */}
        {view.carrierService && (
          <Detail label="Доставка через" value={view.carrierService} />
        )}
      </div>

      {/* Date */}
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
        Создан: {new Date(order.createdAtUtc).toLocaleString('ru-RU')}
      </p>

      {/* Actions — TASK-253: one action-oriented primary per status,
          secondary affordances, or an explicit waiting state. */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
        {view.primary && (
          <button
            className={view.primary.kind === 'accept' || view.primary.kind === 'attachResult' || view.primary.kind === 'ship' ? 'btn btn-primary' : 'btn btn-secondary'}
            style={{ height: 40, padding: '8px 20px' }}
            onClick={runPrimary}
          >
            {view.primary.kind === 'attachResult' && showResultForm
              ? 'Скрыть'
              : view.primary.kind === 'ship' && showShipForm
                ? pickup ? 'Скрыть выдачу' : 'Скрыть отправку'
                : view.primary.label}
          </button>
        )}

        {view.secondary.includes('reject') && (
          <button className="btn btn-tertiary" style={{ height: 40, padding: '8px 20px' }} onClick={() => onReject(order.orderId)}>
            Отклонить
          </button>
        )}
        {view.secondary.includes('offer') && (
          <button className="btn btn-secondary" style={{ height: 40, padding: '8px 20px' }} onClick={() => setShowOfferForm((v) => !v)}>
            {showOfferForm ? 'Скрыть' : 'Предложить условия'}
          </button>
        )}
        {view.secondary.includes('progressPhotos') && (
          <button className="btn btn-tertiary" style={{ height: 40, padding: '8px 20px' }} onClick={() => setShowProgressForm((v) => !v)}>
            {showProgressForm ? 'Скрыть' : '📷 Фото процесса'}
          </button>
        )}
        {view.secondary.includes('addTracking') && (
          <button className="btn btn-tertiary" style={{ height: 40, padding: '8px 20px' }} onClick={() => setShowTrackingForm((v) => !v)}>
            {showTrackingForm ? 'Скрыть' : '✉️ Добавить трек-номер'}
          </button>
        )}

        {view.waiting && (
          <span
            style={{
              fontSize: 'var(--fs-caption)',
              color: view.waiting.tone === 'danger'
                ? 'var(--color-danger)'
                : view.waiting.tone === 'success'
                  ? 'var(--color-success)' // TASK-258: dated terminal note
                  : 'var(--color-text-secondary)',
              alignSelf: 'center',
            }}
          >
            {view.waiting.label}
          </span>
        )}
      </div>

      {/* TASK-235: publish result — photos (URL) + note; moves Printing → Printed */}
      {status === 'Printing' && showResultForm && (
        <AttachResultForm
          orderId={order.orderId}
          onDone={() => {
            setShowResultForm(false);
            onChanged();
          }}
        />
      )}

      {/* TASK-250: share in-progress photos without publishing the result */}
      {(status === 'Accepted' || status === 'Printing') && showProgressForm && (
        <ProgressPhotosForm
          orderId={order.orderId}
          onDone={() => {
            setShowProgressForm(false);
            onChanged();
          }}
        />
      )}

      {/* TASK-240/253: customer confirmed — explicit ship control with
          tracking, revealed by the primary action. */}
      {status === 'Printed' && order.customerConfirmedAtUtc && showShipForm && (
        <ShipForm
          orderId={order.orderId}
          pickup={pickup}
          deliveryCarrier={order.deliveryCarrier}
          onChanged={() => {
            setShowShipForm(false);
            onChanged();
          }}
        />
      )}

      {/* audit4-A3 (b10d4e95): one-shot post-ship tracking patch — closes the
          promise the no-tracking Shipped email made to the customer; the API
          re-sends the «Заказ отправлен» email with the fresh number. */}
      {status === 'Shipped' && !order.trackingNumber?.trim() && showTrackingForm && (
        <TrackingPatchForm
          orderId={order.orderId}
          deliveryCarrier={order.deliveryCarrier}
          onChanged={() => {
            setShowTrackingForm(false);
            onChanged();
          }}
        />
      )}

      {/* TASK-246: negotiation — customer counter to answer, or a new proposal. */}
      {status === 'Pending' && openOffer && !openOffer.isFromProvider && (
        <div
          className="card"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
            padding: 'var(--space-md)',
            background: 'var(--color-bg)',
            border: '1px solid var(--color-separator)',
          }}
        >
          <h4 style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)' }}>
            Встречное предложение клиента
          </h4>
          <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)', color: 'var(--color-primary)' }}>
              {openOffer.proposedPriceRub}₽
            </span>
            {openOffer.proposedDeliveryUtc && (
              <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
                Доставка до {new Date(openOffer.proposedDeliveryUtc).toLocaleDateString('ru-RU')}
              </span>
            )}
          </div>
          <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>{openOffer.message}</p>
          {offerError && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{offerError}</p>}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary"
              style={{ height: 40, padding: '8px 20px' }}
              disabled={busyOffer}
              onClick={() => void respondOffer(true)}
            >
              {busyOffer ? 'Принятие…' : 'Принять условия клиента'}
            </button>
            <button
              className="btn btn-tertiary"
              style={{ height: 40, padding: '8px 20px' }}
              disabled={busyOffer}
              onClick={() => void respondOffer(false)}
            >
              Отклонить
            </button>
          </div>
        </div>
      )}

      {status === 'Pending' && openOffer && openOffer.isFromProvider && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
          Вы предложили условия ({openOffer.proposedPriceRub}₽) — ждём ответа клиента.
        </p>
      )}
      {status === 'Pending' && !openOffer && showOfferForm && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-sm)',
            paddingTop: 'var(--space-sm)',
            borderTop: '1px solid var(--color-separator)',
          }}
        >
          <CounterOfferForm
            orderId={order.orderId}
            title="Предложить условия заказа"
            submitLabel="Отправить предложение"
            onDone={() => {
              setShowOfferForm(false);
              onChanged();
            }}
            onCancel={() => setShowOfferForm(false)}
          />
        </div>
      )}
    </div>
  );
}

/* ── Attach result form ───────────────────────────── */

function AttachResultForm({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const [photos, setPhotos] = useState<ResultPhotoDraft[]>([{ url: '', caption: '' }]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploadingRow, setUploadingRow] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validUrls = photos.map((p) => p.url.trim()).filter((u) => u.length > 0);

  // TASK-241: upload the picked file to /api/v1/media/order-results and
  // put the hosted /media/… URL into the row (manual paste stays possible).
  const uploadFile = async (file: File, row: number) => {
    setUploadingRow(row);
    setError(null);
    try {
      const media = await api.uploadOrderResultPhoto(file);
      setPhotos((ps) => ps.map((p, j) => (j === row ? { ...p, url: media.url } : p)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить фото');
    } finally {
      setUploadingRow(null);
    }
  };

  const submit = async () => {
    if (validUrls.length === 0) {
      setError('Добавьте хотя бы одну ссылку на фото');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.attachProviderOrderResult(
        orderId,
        photos.filter((p) => p.url.trim()).map((p) => ({ url: p.url.trim(), caption: p.caption.trim() || null })),
        note.trim() || null,
      );
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить результат');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    fontSize: 'var(--fs-body)',
    fontFamily: 'inherit',
    background: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        {/* TASK-254: the status name matches the badges the provider actually
            sees — «Напечатан» existed nowhere else in the UI. */}
        Загрузите фото готовой печати (JPEG/PNG/WebP, до 10 МБ) или вставьте ссылку вручную. После публикации заказ перейдёт в статус «Ждёт подтверждения» — дальше клиент проверит фото и подтвердит результат.
      </p>
      {photos.map((photo, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
          <label
            className="btn btn-tertiary"
            style={{ height: 40, padding: '8px 12px', cursor: uploadingRow === i ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
          >
            {uploadingRow === i ? '⏳ …' : '📷 Файл'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              disabled={uploadingRow !== null || busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void uploadFile(file, i);
              }}
            />
          </label>
          <input
            style={inputStyle}
            placeholder="/media/… или https://…/photo.jpg"
            value={photo.url}
            onChange={(e) => setPhotos((ps) => ps.map((p, j) => (j === i ? { ...p, url: e.target.value } : p)))}
          />
          <input
            style={{ ...inputStyle, maxWidth: 180 }}
            placeholder="Подпись"
            value={photo.caption}
            onChange={(e) => setPhotos((ps) => ps.map((p, j) => (j === i ? { ...p, caption: e.target.value } : p)))}
          />
          {photos.length > 1 && (
            <button
              className="btn btn-tertiary"
              style={{ height: 40, padding: '8px 12px' }}
              onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      <div>
        <button className="btn btn-tertiary" style={{ height: 36, padding: '6px 14px' }} onClick={() => setPhotos((ps) => [...ps, { url: '', caption: '' }])}>
          + Ещё фото
        </button>
      </div>
      <textarea
        style={{ ...inputStyle, resize: 'vertical' }}
        placeholder="Комментарий к результату (необязательно)"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div>
        <button className="btn btn-primary" style={{ height: 40, padding: '8px 20px' }} disabled={busy || validUrls.length === 0} onClick={() => void submit()}>
          {busy ? 'Публикация…' : 'Опубликовать результат'}
        </button>
      </div>
    </div>
  );
}

/* ── Ship form (TASK-240) ─────────────────────── */

/* ── In-progress photos form (TASK-250) ───────── */

function ProgressPhotosForm({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const [photos, setPhotos] = useState<ResultPhotoDraft[]>([{ url: '', caption: '' }]);
  const [busy, setBusy] = useState(false);
  const [uploadingRow, setUploadingRow] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validUrls = photos.map((p) => p.url.trim()).filter((u) => u.length > 0);

  // TASK-241 upload pipeline — progress photos reuse the same media serving.
  const uploadFile = async (file: File, row: number) => {
    setUploadingRow(row);
    setError(null);
    try {
      const media = await api.uploadOrderResultPhoto(file);
      setPhotos((ps) => ps.map((p, j) => (j === row ? { ...p, url: media.url } : p)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить фото');
    } finally {
      setUploadingRow(null);
    }
  };

  const submit = async () => {
    if (validUrls.length === 0) {
      setError('Добавьте хотя бы одну ссылку на фото');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.attachProviderOrderProgress(
        orderId,
        photos.filter((p) => p.url.trim()).map((p) => ({ url: p.url.trim(), caption: p.caption.trim() || null })),
      );
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отправить фото процесса');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    fontSize: 'var(--fs-body)',
    fontFamily: 'inherit',
    background: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        Поделитесь фото процесса печати (JPEG/PNG/WebP, до 10 МБ) или вставьте ссылку вручную. Заказ останется в текущем статусе — это не публикация результата.
      </p>
      {photos.map((photo, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
          <label
            className="btn btn-tertiary"
            style={{ height: 40, padding: '8px 12px', cursor: uploadingRow === i ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
          >
            {uploadingRow === i ? '⏳ …' : '📷 Файл'}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              disabled={uploadingRow !== null || busy}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file) void uploadFile(file, i);
              }}
            />
          </label>
          <input
            style={inputStyle}
            placeholder="/media/… или https://…/photo.jpg"
            value={photo.url}
            onChange={(e) => setPhotos((ps) => ps.map((p, j) => (j === i ? { ...p, url: e.target.value } : p)))}
          />
          <input
            style={{ ...inputStyle, maxWidth: 180 }}
            placeholder="Подпись"
            value={photo.caption}
            onChange={(e) => setPhotos((ps) => ps.map((p, j) => (j === i ? { ...p, caption: e.target.value } : p)))}
          />
          {photos.length > 1 && (
            <button
              className="btn btn-tertiary"
              style={{ height: 40, padding: '8px 12px' }}
              onClick={() => setPhotos((ps) => ps.filter((_, j) => j !== i))}
            >
              ✕
            </button>
          )}
        </div>
      ))}
      <div>
        <button className="btn btn-tertiary" style={{ height: 36, padding: '6px 14px' }} onClick={() => setPhotos((ps) => [...ps, { url: '', caption: '' }])}>
          + Ещё фото
        </button>
      </div>
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div>
        <button className="btn btn-secondary" style={{ height: 40, padding: '8px 20px' }} disabled={busy || validUrls.length === 0} onClick={() => void submit()}>
          {busy ? 'Отправка…' : 'Отправить фото процесса'}
        </button>
      </div>
    </div>
  );
}

function ShipForm({ orderId, pickup, deliveryCarrier, onChanged }: { orderId: string; pickup: boolean; deliveryCarrier?: string | null; onChanged: () => void }) {
  const [tracking, setTracking] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.updateProviderOrderStatus(
        orderId,
        'Shipped',
        tracking.trim() || undefined,
        deliveryDate ? new Date(`${deliveryDate}T00:00:00Z`).toISOString() : undefined,
      );
      onChanged();
    } catch (e) {
      // TASK-252: pickup handoff copy — the action marks the order ready for
      // collection, not a courier dispatch (no API change, copy only).
      setError(e instanceof Error ? e.message : pickup ? 'Не удалось обновить статус' : 'Не удалось отправить заказ');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: CSSProperties = {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    fontSize: 'var(--fs-body)',
    fontFamily: 'inherit',
    background: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-success)', fontWeight: 'var(--fw-semibold)' }}>
        {pickup
          ? 'Клиент подтвердил результат — подготовьте заказ к выдаче в пункте самовывоза.'
          : 'Клиент подтвердил результат — можно отправлять.'}
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, flex: '1 1 200px' }}
          placeholder="Трек-номер (необязательно)"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
        />
        <input
          style={{ ...inputStyle, flex: '0 1 180px' }}
          type="date"
          title={pickup ? 'Дата готовности к выдаче' : 'Ожидаемая дата доставки'}
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
        />
      </div>
      {/* 31c9ace7: live carrier hint — entry and display never disagree. */}
      <TrackingInputHint draft={tracking} deliveryCarrier={deliveryCarrier} />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div>
        <button className="btn btn-primary" style={{ height: 40, padding: '8px 20px' }} disabled={busy} onClick={() => void submit()}>
          {pickup ? (busy ? 'Готовим…' : 'Готово к выдаче') : busy ? 'Отправка…' : 'Отправить заказ'}
        </button>
      </div>
    </div>
  );
}


/* ── 31c9ace7: live tracking-entry hint ────── */

/* Shows the provider how the number will resolve the moment it is saved:
 * known carrier → the customer gets a labeled row + «Отследить» deep link
 * (or an honest label-only row for carriers without a public number page,
 * e.g. Ozon); unknown shape → the number is kept, but without a link.
 * Reuses the pure shippedTrackingView — entry and display never disagree. */
function TrackingInputHint({ draft, deliveryCarrier }: { draft: string; deliveryCarrier?: string | null }) {
  const trimmed = draft.trim();
  if (trimmed.length === 0) return null;
  const view = buildShippedTrackingView({ trackingNumber: trimmed, deliveryCarrier });
  if (view.trackUrl != null && view.carrierLabel != null)
    return <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{view.carrierLabel} — клиент получит трек-номер со ссылкой «Отследить».</p>;
  if (view.carrierLabel != null)
    return <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>{view.carrierLabel} — номер сохранится с названием перевозчика, без прямой ссылки.</p>;
  return <p style={{ margin: 0, fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Перевозчик не распознан — номер сохранится без ссылки на отслеживание.</p>;
}

/* ── Post-ship tracking patch (audit4-A3) ────── */

/* One-shot: the API accepts it only while Shipped and tracking is empty.
 * Re-notifies the customer through the existing OrderShipped email. */
function TrackingPatchForm({ orderId, deliveryCarrier, onChanged }: { orderId: string; deliveryCarrier?: string | null; onChanged: () => void }) {
  const [tracking, setTracking] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!tracking.trim()) {
      setError('Введите трек-номер');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.patchProviderOrderTracking(
        orderId,
        tracking.trim(),
        deliveryDate ? new Date(`${deliveryDate}T00:00:00Z`).toISOString() : undefined,
      );
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить трек-номер');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: CSSProperties = {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    fontSize: 'var(--fs-body)',
    fontFamily: 'inherit',
    background: 'var(--color-surface)',
    color: 'var(--color-text-primary)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)', paddingTop: 'var(--space-sm)', borderTop: '1px solid var(--color-separator)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
        Клиент получил письмо без трек-номера — добавьте его, и мы отправим письмо ещё раз, уже с номером. Изменить номер после этого будет нельзя.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
        <input
          style={{ ...inputStyle, flex: '1 1 200px' }}
          placeholder="Трек-номер"
          value={tracking}
          onChange={(e) => setTracking(e.target.value)}
        />
        <input
          style={{ ...inputStyle, flex: '0 1 180px' }}
          type="date"
          title="Ожидаемая дата доставки (необязательно)"
          value={deliveryDate}
          onChange={(e) => setDeliveryDate(e.target.value)}
        />
      </div>
      {/* 31c9ace7: live carrier hint — entry and display never disagree. */}
      <TrackingInputHint draft={tracking} deliveryCarrier={deliveryCarrier} />
      {error && <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>}
      <div>
        <button className="btn btn-secondary" style={{ height: 40, padding: '8px 20px' }} disabled={busy || !tracking.trim()} onClick={() => void submit()}>
          {busy ? 'Сохранение…' : 'Сохранить и уведомить клиента'}
        </button>
      </div>
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────── */

function StatusBadge({ status, confirmed = false, pickup = false }: { status: OrderStatus; confirmed?: boolean; pickup?: boolean }) {
  const map: Partial<Record<OrderStatus, string>> = {
    Pending: 'badge-warning',
    Accepted: 'badge-warning',
    Printing: 'badge-warning',
    Printed: 'badge-warning',
    Shipped: 'badge-success',
    Completed: 'badge-success',
    Cancelled: 'badge-danger',
    Disputed: 'badge-danger',
  };
  const cls = map[status] ?? 'badge-warning';
  const labels: Record<OrderStatus, string> = {
    Pending: 'Ожидает',
    Accepted: 'Принят',
    Printing: 'Печатается',
    Printed: 'Ждёт подтверждения',
    Shipped: 'Отправлен',
    Completed: 'Завершён',
    Cancelled: 'Отменён',
    Disputed: 'Спор',
  };
  // TASK-252: method-aware badge copy — a confirmed Printed pickup order is
  // waiting on the collection handoff (not a dispatch), and Shipped for
  // pickup means ready at the point («Готов к выдаче", TASK-248 stage parity).
  const methodLabel =
    status === 'Printed' && confirmed
      ? pickup ? 'Ждёт выдачи' : 'Ждёт отправки'
      : status === 'Shipped' && pickup
        ? 'Готов к выдаче'
        : labels[status];
  return <span className={`badge ${cls}`}>{methodLabel}</span>;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <span style={{
      padding: '4px 10px',
      borderRadius: 'var(--radius-sm)',
      background: 'var(--color-bg)',
      fontSize: 'var(--fs-caption)',
      color: 'var(--color-text-secondary)',
    }}>
      {label}: {value}
    </span>
  );
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min} мин`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}ч ${m}мин` : `${h}ч`;
}

/* ecfe2257 (Artem 2026-08-28 §5): the queue-card color cue — concrete color
   names the spool; multicolor pins «уточните слоты в переписке» (no up-front
   mapping); plain any-color keeps the legacy silence (nothing to load). */
export function providerColorCue(printColor: string | null | undefined, mode: number | undefined): string {
  const color = (printColor ?? '').trim();
  if (color.length > 0) return ` · цвет: ${color}`;
  if (mode === 1) return ' · многоцветная — слоты уточняются в переписке';
  return '';
}
